/**
 * PortfolioSummaryCard - Gain/loss, today's change, and cash position cards.
 *
 * Total Value is now shown in the hero section of PortfolioPage.
 * Cards here show secondary metrics with left-edge color accents.
 */

import { TrendingUp, TrendingDown, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/ui/Tooltip';
import type { PortfolioSummary } from '@/types/portfolio';

interface PortfolioSummaryCardProps {
  summary: PortfolioSummary;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function SummaryCard({
  accentColor,
  children,
}: {
  accentColor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative bg-surface-light rounded-xl p-5 border border-border overflow-hidden">
      <div className={cn('absolute top-0 left-0 bottom-0 w-0.5', accentColor)} />
      {children}
    </div>
  );
}

export function PortfolioSummaryCard({ summary }: PortfolioSummaryCardProps) {
  const isGainPositive = summary.total_gain >= 0;
  const isTodayPositive = summary.today_gain >= 0;
  const hasCash = (summary.cash_balance ?? 0) > 0;
  const cashBalance = summary.cash_balance ?? 0;

  return (
    <div className={cn(
      'grid grid-cols-1 gap-4',
      hasCash ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
    )}>
      {/* Total Gain/Loss */}
      <SummaryCard accentColor={isGainPositive ? 'bg-green-400' : 'bg-red-400'}>
        <div className="flex items-center gap-1.5 mb-2">
          <p className="text-xs text-text-muted uppercase tracking-wider">Total Gain/Loss</p>
          <InfoTooltip
            content="Cumulative gain or loss since purchase. Current Value minus Total Cost Basis."
            position="top"
          />
        </div>
        <div className="flex items-center gap-2">
          {isGainPositive ? (
            <TrendingUp className="w-5 h-5 text-green-400" />
          ) : (
            <TrendingDown className="w-5 h-5 text-red-400" />
          )}
          <p className={cn(
            'text-3xl font-bold',
            isGainPositive ? 'text-green-400' : 'text-red-400'
          )}>
            {formatCurrency(summary.total_gain)}
          </p>
        </div>
        <p className={cn(
          'text-sm mt-1',
          isGainPositive ? 'text-green-400/70' : 'text-red-400/70'
        )}>
          {isGainPositive ? '+' : ''}{summary.total_gain_pct.toFixed(2)}%
        </p>
      </SummaryCard>

      {/* Today's Change */}
      <SummaryCard accentColor={isTodayPositive ? 'bg-green-400' : 'bg-red-400'}>
        <div className="flex items-center gap-1.5 mb-2">
          <p className="text-xs text-text-muted uppercase tracking-wider">Today's Change</p>
          <InfoTooltip
            content="Net change in portfolio value during today's trading session."
            position="top"
          />
        </div>
        <div className="flex items-center gap-2">
          {isTodayPositive ? (
            <TrendingUp className="w-5 h-5 text-green-400" />
          ) : (
            <TrendingDown className="w-5 h-5 text-red-400" />
          )}
          <p className={cn(
            'text-3xl font-bold',
            isTodayPositive ? 'text-green-400' : 'text-red-400'
          )}>
            {formatCurrency(summary.today_gain)}
          </p>
        </div>
      </SummaryCard>

      {/* Cash / Money Market (conditional) */}
      {hasCash && (
        <SummaryCard accentColor="bg-[#D4A574]">
          <div className="flex items-center gap-1.5 mb-2">
            <p className="text-xs text-text-muted uppercase tracking-wider">Cash / Money Market</p>
            <InfoTooltip
              content="Money market and cash sweep balances (e.g., SPAXX). Not included in gain/loss calculations."
              position="top"
            />
          </div>
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#D4A574]" />
            <p className="text-3xl font-bold text-text-primary">
              {formatCurrency(cashBalance)}
            </p>
          </div>
          <p className="text-xs text-text-muted mt-1">
            {((cashBalance / (summary.total_value + cashBalance)) * 100).toFixed(1)}% of total
          </p>
        </SummaryCard>
      )}
    </div>
  );
}
